import React, { useMemo } from 'react';

import { Button, Pill, useAdaptive } from '@skillarea/lib-frontend';
import {
  ELeague,
  GameReturnDto,
  getSafeLeagueForGame,
  getStakeCapCentsForLeague,
} from '@skillarea/lib-shared';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import styles from './GameCard.module.scss';

import { MinimumAgeModal } from '~/components/MinimumAgeModal/MinimumAgeModal';
import { useLocalizedGame } from '~/hooks/useLocalizedGame';
import { useMinimumAgeGuard } from '~/hooks/useMinimumAgeGuard';
import { useOnboardStore } from '~/stores/onboardStore';
import { useUserGameStats } from '~/stores/userStore';
import { useIsMaintenanceEnabled } from '~/stores/maintenanceStore';

const CALIBRATION_GAMES_THRESHOLD = 5;

export const GameCard: React.FC<GameReturnDto> = (game) => {
  const { i18n, t } = useTranslation('games');
  const localizedGame = useLocalizedGame(game);
  const { isMobile } = useAdaptive();
  const isLoggedIn = useOnboardStore((s) => s.isLoggedIn);
  const isMaintenanceEnabled = useIsMaintenanceEnabled();
  const gameStats = useUserGameStats();
  const {
    closeMinimumAgeModal,
    isMinimumAgeModalOpen,
    isUnderMinimumAge,
    minimumAge,
    showMinimumAgeModal,
  } = useMinimumAgeGuard();
  const maxStakeDollars = useMemo(() => {
    if (!isLoggedIn) {
      return Math.floor(getStakeCapCentsForLeague(ELeague.IRON) / 100);
    }

    const minLeague =
      gameStats?.minLeaguePerGame?.[String(game.id)] ?? ELeague.IRON;
    const gamesPlayedRaw = gameStats?.gamesPerGame?.[game.id];
    const gamesPlayed =
      typeof gamesPlayedRaw === 'number' && Number.isFinite(gamesPlayedRaw)
        ? gamesPlayedRaw
        : 0;

    const effectiveLeague =
      minLeague === ELeague.IRON && gamesPlayed < CALIBRATION_GAMES_THRESHOLD
        ? ELeague.IRON
        : getSafeLeagueForGame(gameStats, game.id, 1000);

    return Math.floor(getStakeCapCentsForLeague(effectiveLeague) / 100);
  }, [game.id, gameStats, isLoggedIn]);

  const playButtonProps: React.ComponentProps<typeof Button> =
    isMaintenanceEnabled
      ? {
          disabled: true,
          type: 'button',
        }
      : isUnderMinimumAge
        ? {
            onClick: showMinimumAgeModal,
            type: 'button',
          }
        : {
            as: 'link',
            hash: 'play',
            to: `/games/${game.slug}`,
          };

  return (
    <div className={styles.gameCard} data-game-slug={game.slug}>
      <Link
        aria-label={t('ui.goToGameAria', { name: localizedGame.name })}
        className="absolute inset-0 z-[1]"
        params={{ gameId: game.slug }}
        to="/games/$gameId"
      />
      <div className={styles.gameImageContainer}>
        <img
          alt={localizedGame.name}
          className={styles.gameImage}
          decoding="async"
          loading="lazy"
          src={game.image}
        />
        <div className={styles.overlay}></div>
      </div>

      <div className={styles.content} style={{ pointerEvents: 'none' }}>
        <h3 className={styles.gameTitle}>{localizedGame.name}</h3>

        {!isMobile && (
          <div className={styles.gameInfo}>
            <Pill icon="coins" size="m">
              {t('ui.maxEntryFee', {
                amount: maxStakeDollars.toLocaleString(i18n.language),
              })}
            </Pill>
          </div>
        )}
        <Button
          className={`${styles.playButton} ${styles.playButtonAccent}`}
          color="accent"
          data-testid="play-button-card"
          size={isMobile ? 's' : 'm'}
          style={{ pointerEvents: 'auto' }}
          variant="filled"
          {...playButtonProps}
        >
          {t('ui.play')}
        </Button>

        <MinimumAgeModal
          isOpen={isMinimumAgeModalOpen}
          minimumAge={minimumAge}
          onClose={closeMinimumAgeModal}
        />
      </div>
    </div>
  );
};
